import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, ViewChild, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, FormGroupDirective, ControlContainer, AbstractControl } from '@angular/forms';
import _ from 'lodash';
import moment from 'moment';
import { debounceTime, distinctUntilChanged, Observable, of, pairwise, startWith } from 'rxjs';
import { IPortalConfig, IAnswers, IForm, IPatientFormQuestionnaire, IQuestions, IResponses, ISaveAnswer, UserAccount } from 'src/app/models';
import { SecurityService, PatientFormsService, PortalService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { Helpers } from 'src/app/helpers';

@Component({
    selector: 'medpt-dynamic-form-builder',
    templateUrl: './dynamic-form-builder.component.html',
    styleUrls: ['./dynamic-form-builder.component.scss'],
    standalone: false
})
export class DynamicFormBuilderComponent implements OnInit, OnDestroy {


  @Output() onSubmit = new EventEmitter();
  @Input() fields!: IPatientFormQuestionnaire;
  @Input() submitted: boolean = false;

  portalConfig!: IPortalConfig;
  currentUser!: UserAccount;
  questions: Observable<any[]> = of([]);
  form!: FormGroup;
  formInfo!: IForm;
  formunsubcribe: any;
  disabled: boolean = false;
  private fieldsCtrls: any = {};

  constructor(
    private controlContainer: ControlContainer,
    private portalService: PortalService,
    private pfService: PatientFormsService,
    private securityService: SecurityService,
    private cd: ChangeDetectorRef) {

  }

  ngOnInit() {

    this.portalConfig = this.portalService.portalConfiguation.getValue();
    this.currentUser = this.securityService.currentUserValue;
    // Set our form property to the parent control
    // (i.e. FormGroup) that was passed to us, so that our
    // view can data bind to it
    this.form = <FormGroup>this.controlContainer.control;

    this.formInfo = {
      Disabled: this.fields.Form.Disabled,
      DisplayOrdinals: this.fields.Form.DisplayOrdinals,
      FinishText: this.fields.Form.FinishText,
      FormCreatedByUserID: this.fields.Form.FormCreatedByUserID,
      FormID: this.fields.Form.FormID,
      FormName: this.fields.Form.FormName,
      HasAnswers: this.fields.Form.HasAnswers,
      HasSignature: this.fields.Form.HasSignature,
      IntroText: this.fields.Form.IntroText,
      mid: this.fields.Form.mid,
      Preview: this.fields.Form.Preview,
      Questions: [],
      SiteAssetID: this.fields.Form.SiteAssetID,
      SiteAssetStatusTypeID: this.fields.Form.SiteAssetStatusTypeID,
      SiteID: this.fields.Form.SiteID,
      TestID: this.fields.Form.TestID
    };
    //console.log('Form Disabled', this.formInfo);
    this.fields.Form.Questions = _.orderBy(this.fields.Form.Questions, ['QuestionOrdinal'], ['asc']);

    this.fields.Form.Questions.forEach((f: IQuestions) => {
      f.uniquename = 'question_' + f.QuestionID;

      f.controlDisabled = false;
      if (f.IsLinkedDisabled != null && !this.formInfo.Disabled) {
        f.controlDisabled = f.IsLinkedDisabled;
      } else {
        f.controlDisabled = this.formInfo.Disabled;
      }
      //console.log('Question Disabled', f.IsLinkedDisabled);
      f.controlVisibled = true;
      f.parentVisibleApplied = false;
      f.isArray = (f.QuestionType == "Matrix" || f.QuestionType == "MultipleAnswer");

      if (f.QuestionType == 'Rated' || f.QuestionType == 'TrueFalse' || f.QuestionType == 'YesNo' || f.QuestionType == 'MultipleChoice') {
        f.QuestionType = "MultipleChoice";
      }
      if (f.isArray) {
        <Array<any>>(f.QuestionType == 'Matrix' ? f.MatrixQuestion.MatrixAnswers : f.Answers).forEach((opt: any) => {
          opt.uniquename = 'option_' + f.QuestionID + '_' + (f.QuestionType == 'Matrix' ? opt.FormAnswerDefID : opt.FormAnswerID);
        });
      }
    });

    this.initFormControls();

    this.fields.Form.Questions.forEach(quest => {

      if (quest.MergeFieldName != 'Date' && quest.MergeFieldName != 'SiteName' && quest.MergeFieldName != 'Email') {

        if (quest.QuestionType == "ShortAnswer" || quest.QuestionType == "LongAnswer" || quest.MergeFieldName == "FormCounter") {
          this.form.controls[quest.uniquename].valueChanges.pipe(
            startWith(null),
            pairwise(),
            debounceTime(1500),
            distinctUntilChanged()
          )
            .subscribe(([previousValue, nextValue]: [any, any]) => {
              this.detectFormChanges(quest.uniquename, previousValue, nextValue);
            });
        }
        else {
          this.form.controls[quest.uniquename].valueChanges.pipe(
            startWith(null),
            pairwise()
          )
            .subscribe(([previousValue, nextValue]: [any, any]) => {
              this.detectFormChanges(quest.uniquename, previousValue, nextValue);
            });
        }
      }

    });
  }

  //Initial Changes
  initFormControls() {

    this.fields.Form.Questions.forEach((f: any, ind: number) => {
      let response = this.fields.Responses == null ? [] : (<Array<any>>this.fields.Responses).filter((item: any) => { return item.FormQuestionID == f.QuestionID; });
      let value: any = null;
      let valueSelected: boolean = false;

      if (f.isArray) {

        this.fieldsCtrls[f.uniquename] = (f.IsRequired && f.controlVisibled && !f.controlDisabled) ? new FormArray([], [Validators.required]) : new FormArray([]);

        if (response.length > 0) {
          let res = response[0].FormAnswerIDs.split(',');
          if (res != null) {
            value = _.map(res, (item) => {
              let frmCtrl = (f.IsRequired && f.controlVisibled && !f.controlDisabled) ? new FormControl({ value: item, disabled: f.controlDisabled }, [Validators.required]) : new FormControl({ value: item, disabled: f.controlDisabled })
              this.fieldsCtrls[f.uniquename].push(frmCtrl);
              return parseInt(item);
            });
          }
        }
      }

      else {
        value = response.length == 0 ? null : (f.MergeFieldName == 'Date' || f.QuestionType == 'Date' || response[0].FormAnswerIDs == "0") ? response[0].ResponseChar : parseInt(response[0].FormAnswerIDs); //f.Answers.length > 0 ? f.Answers[0].Answer : null;
        value = ((f.MergeFieldName == 'Date' || f.QuestionType == 'Date') && value == "Invalid Date") ? null : value;

        if (!environment.production) {
          console.log(f.QuestionText, f.QuestionID, value);
        }

        if (f.MergeFieldName == 'Date' || f.QuestionType == 'Date') {
          value = value != null ? new Date(moment(value).format("DD-MMM-YYYY")) : '';

          if (f.MergeFieldName == 'Date' && value == '') {
            value = moment(this.fields.ServerTime).format("DD-MMM-YYYY") == "01-Jan-0001" ? new Date() : new Date(moment(this.fields.ServerTime).format("DD-MMM-YYYY"));
          }
        } else if (f.QuestionType == 'Time') {
          if (value != '' && value != null && value != undefined)
            value = new Date(moment().format('DD-MMM-YYYY ') + value);
        } else if (f.MergeFieldName == 'SiteName') {
          value = value != null ? value : this.portalConfig.SiteName;
          value = Helpers.decodeEntity(value);
        } else if (f.MergeFieldName == 'SubmittedBy') {
          value = value != null ? value : this.currentUser.FullName;
          value = Helpers.decodeEntity(value);
        } else if (f.MergeFieldName == 'Email') {
          value = value != null ? value : this.currentUser.UserName;
          value = Helpers.decodeEntity(value);
        } else {
          value = isNaN(value) ? Helpers.decodeEntity(value) : value;
        }

        value = value == null ? '' : value;
        let config = { value: value, disabled: (f.MergeFieldName == 'Date' || f.MergeFieldName == 'SubmittedBy' || f.MergeFieldName == 'SiteName' || f.MergeFieldName == 'Email' || f.controlDisabled) };
        this.fieldsCtrls[f.uniquename] = (f.IsRequired && f.controlVisibled && !f.controlDisabled) ? new FormControl(config, [Validators.required]) : new FormControl(config);

        if (f.MergeFieldName == 'Date' || f.QuestionType == 'Date')
          value = moment(value).format('MM/DD/YYYY');
      }
      var MatchedResponse = _.filter(this.fields.Responses, (item) => { return item.FormQuestionID == f.QuestionID; })
      if (MatchedResponse.length > 0) {
        this.fields.Form.Questions[ind].response = MatchedResponse[0];
      }

      <Array<any>>(f.QuestionType == 'Matrix' ? f.MatrixQuestion.MatrixAnswers : f.Answers).forEach((opt: any) => {
        if (f.isArray) {
          opt.checked = valueSelected = _.filter(value, (item) => { return parseInt(item) == (f.QuestionType == 'Matrix' ? opt.FormAnswerDefID : opt.FormAnswerID); }).length > 0;
        } else {
          valueSelected = (value == ((f.MergeFieldName == 'Date' || f.QuestionType == 'Date') ? opt.Answer : opt.FormAnswerID));
        }

        if (opt.LinkedId != 0 && opt.IsGroupLink) {
          let array = _.filter(this.fields.Form.Questions, (item: any) => { return item.QuestionGroupID == opt.LinkedId });
          if (array.length > 0) {
            _.forEach(array, (qItem: any) => {
              let depIndex = _.findIndex(this.fields.Form.Questions, (item: any) => { return item.QuestionID == qItem.QuestionID; });
              if (!this.fields.Form.Questions[depIndex].parentVisibleApplied || valueSelected) {
                this.fields.Form.Questions[depIndex].controlVisibled = f.controlVisibled && valueSelected;
                this.fields.Form.Questions[depIndex].parentVisibleApplied = true;
              }
            });
          }
        }
        else if (opt.LinkedId != 0) {
          let index = _.findIndex(this.fields.Form.Questions, (item: any) => { return item.QuestionID == opt.LinkedId });
          if (index != -1) {
            if (!this.fields.Form.Questions[index].parentVisibleApplied || valueSelected) {
              this.fields.Form.Questions[index].controlVisibled = f.controlVisibled && valueSelected;
              this.fields.Form.Questions[index].parentVisibleApplied = true;
            }
          }
        }
      });

    });
    this.form = new FormGroup(this.fieldsCtrls);
  }

  //Update Changes

  detectFormChanges(key: string, previousValue: any, nextValue: any) {
    if (!environment.production) {
      console.log('KEY IDENTIFIER :: ', key); // Medpt FormControl Identifier
      console.log('PREVIOUS VALUE :: ', previousValue);
      console.log('NEXT VALUE     :: ', nextValue);
      console.log('----------*******--------------');
    }
    let qId = parseInt(key.split('_')[1]);
    let qIndex = _.findIndex(this.fields.Form.Questions, (item: any) => { return item.QuestionID == qId; });
    let question = this.fields.Form.Questions[qIndex];

    let value: any = null;

    if (nextValue != null) {

      let responseChar = "";
      let formAnswerIDs: any = "";

      if (question.MergeFieldName != 'Date' && question.QuestionType != 'Date' && (question.Answers.length > 0 || question.MatrixQuestion?.MatrixAnswers.length > 0)) {
        if (question.QuestionType == 'Matrix')
          responseChar = _.join(_.map(_(question.MatrixQuestion?.MatrixAnswers).keyBy('FormAnswerDefID').at(nextValue).value(), "AnswerDefText"), ' | ');
        else
          responseChar = _.join(_.map(_(question.Answers).keyBy('FormAnswerID').at(nextValue).value(), "Answer"), ' | ');

        formAnswerIDs = nextValue.toString();
        formAnswerIDs = (question.QuestionType == 'SelectAnswer' || question.QuestionType == 'MultipleChoice') ? parseInt(formAnswerIDs) : formAnswerIDs;
      }
      else {
        formAnswerIDs = "";
        debugger;
        if (question.MergeFieldName == 'Date' || question.QuestionType == 'Date')
          responseChar = nextValue.toString() != "" ? moment(nextValue.toString()).format("DD-MMM-YYYY") : "";
        else if (question.QuestionType == 'Time')
          responseChar = nextValue.toString() != "" ? moment(nextValue.toString()).format("hh:mm A") : "";
        else
          responseChar = nextValue.toString();
      }

      let obj: IResponses = {
        _FormResponseID: 0, FormQuestionID: qId, TestID: "0", ResponseEnumerator: 0,
        UserID: 0, ResponseChar: responseChar, ResponseInt: null, FormAnswerIDs: formAnswerIDs,
        AuditInfoID: 0, AuditInfo: null, FormQuestion: "", ConnectionString: ""
      };

      let responseIndex = _.findIndex(this.fields.Responses, (item) => { return item.FormQuestionID == obj.FormQuestionID; });

      if (responseIndex == -1) {
        this.fields.Responses.push(obj);
      } else {
        this.fields.Responses[responseIndex] = obj;
      }

      if (formAnswerIDs != "") {
        let res = question.isArray ? formAnswerIDs.split(',') : [];
        if (res.length > 0)
          value = _.map(res, (item) => { return parseInt(item); });
        else
          value = parseInt(formAnswerIDs);
      } else {
        value = responseChar;
      }

      let ans: ISaveAnswer = {
        TestId: this.formInfo.TestID,
        QuestionId: qId,
        AnswerId: formAnswerIDs == '' ? "0" : formAnswerIDs,
        Answer: responseChar == '' ? formAnswerIDs : responseChar
      };

      if (!environment.production) {
        console.log('----------Answer--------------');
        console.log(ans);
      }
      if (!this.fields.Form.Preview) {
        this.pfService.saveAnswer(<string>this.portalConfig.SponsorName, <number>this.portalConfig.PortalID, ans).then(() => { }).finally(() => { });
      }

    }
    if (question.Answers != undefined && question.Answers != null)
      this.updateAnswerLinkedQuestion(question, value);

    this.cd.detectChanges();
  }
  updateAnswerLinkedQuestion(question: IQuestions, value: any) {    
    <Array<any>>(question.QuestionType == 'Matrix' ? question.MatrixQuestion?.MatrixAnswers : question.Answers).forEach((opt: any) => {
      let valueSelected: boolean = false;
      opt.checked = false;
      if (question.QuestionType == 'MultipleChoice')
        valueSelected = false;
      if (question.isArray) {
        valueSelected = _.filter(value, (item) => { return parseInt(item) == (question.QuestionType == 'Matrix' ? opt.FormAnswerDefID : opt.FormAnswerID); }).length > 0;
      }
      else {
        if (isNaN(value) && moment(value).isValid()) // question.MergeFieldName == 'Date' || question.QuestionType == 'Date')
          valueSelected = (moment(value).format('MM/DD/YYYY') == opt.Answer);
        else
          valueSelected = valueSelected || (value == (question.QuestionType == 'Matrix' ? opt.FormAnswerDefID : opt.FormAnswerID));
      }
      opt.checked = valueSelected;

      if (opt.LinkedId != 0 && opt.IsGroupLink) {
        this.updateDependentGroupLinkQuestions(question, opt, valueSelected);
      } else if (opt.LinkedId != 0) {
        this.updateDependentLinkQuestions(question, opt, valueSelected);
      }

    });
  }
  updateDependentGroupLinkQuestions(question: IQuestions, answer: IAnswers, valueSelected: boolean) {
    let array = _.filter(this.fields.Form.Questions, (item: IQuestions) => { return item.QuestionGroupID == answer.LinkedId });
    if (array.length > 0) {
      _.forEach(array, (qItem: IQuestions) => {
        let depIndex = _.findIndex(this.fields.Form.Questions, (item: IQuestions) => { return item.QuestionID == qItem.QuestionID; });
        let quest = this.fields.Form.Questions[depIndex];
        this.resetFormControl(quest, question.controlVisibled, valueSelected);
      });
    }
  }
  updateDependentLinkQuestions(question: IQuestions, answer: IAnswers, valueSelected: boolean) {
    let index = _.findIndex(this.fields.Form.Questions, (item: any) => { return item.QuestionID == answer.LinkedId });
    let quest = this.fields.Form.Questions[index];
    this.resetFormControl(quest, question.controlVisibled, valueSelected);
  }
  resetFormControl(quest: IQuestions, parentVisibled: boolean, valueSelected: boolean) {
    if (quest != undefined) {
      quest.controlVisibled = parentVisibled && valueSelected;

      let control = this.form.get(quest.uniquename);
      if (!valueSelected) {
        if (control != null) {
          if (quest.isArray) {
            const checkArray: FormArray = <FormArray>control;
            checkArray.clear({ emitEvent: false });
            checkArray.controls = [];
            checkArray.removeAt(0, { emitEvent: false });
          } else {
            const formControl: FormControl = <FormControl>control;
            formControl.reset(null, { emitEvent: false, onlySelf: true });
          }
        }
      }
      if (control != null) {
        if (quest.IsRequired) {
          if (quest.controlVisibled) {
            control?.addValidators([Validators.required]);
          } else {
            control?.removeValidators([Validators.required]);
          }
        }
        control.updateValueAndValidity({ emitEvent: false, onlySelf: true });
      }
      this.updateAnswerLinkedQuestion(quest, null);
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['submitted'] != undefined) {
      this.submitted = changes['submitted'].currentValue;
      if (this.submitted) {
        this.form.markAllAsTouched();
        //this.getFormErrors(this.form);
        this.onSubmit.emit({ formValid: !this.form.invalid, formValue: this.form.value, fields: this.fields });
      }
    }
  }

  ngOnDestroy() {
    if (this.formunsubcribe != undefined)
      this.formunsubcribe();
  }

  getFormErrors(form: AbstractControl) {
    if (form instanceof FormControl) {
      // Return FormControl errors or null
      console.log('form.errors', form.errors);
      return form.errors ?? null;
    }
    if (form instanceof FormGroup) {
      const groupErrors = form.errors;
      console.log('formgroup.errors', form.errors);
      // Form group can contain errors itself, in that case add'em
      const formErrors = groupErrors ? { groupErrors } : {};
      Object.keys(form.controls).forEach((key) => {
        // Recursive call of the FormGroup fields
        let key1 = form.get(key);
        if (key1 != null) {
          const error = this.getFormErrors(key1);
          if (error !== null) {
            console.log('error', error);
            // Only add error if not null
            //formErrors[key] = error;
          }
        }
      });
      // Return FormGroup errors or null
      return Object.keys(formErrors).length > 0 ? formErrors : null;
    }
    return null;
  }
}
